import React, { useEffect, useState, useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { StringParam, useQueryParam } from "use-query-params";
import { FormMixPanel, SignUpFormData } from "@types";
import PasswordValidationLabel from "./PasswordValidationLabel/PasswordValidationLabel";
import PhoneInputMask from "./PhoneInput/phoneInputMask";
import HorizontalRule from "../UI/HorizontalRule";
import Helpers from "./helpers";
import "./recaptcha.scss";
import {
  formDataInitialState,
  initialInputState,
  initialMXPInputState,
  pageOptions,
} from "./dataOptions";
import TermsAndServices from "./TermsAndServices";
import { FormError, FormInput, FormValue } from "@types";
import validationClasses from "./helpers/validations/validationClasses";
import { Cookie } from "@utils/Cookie";
import FormCookieMessage from "./FormCookieMessage/FormCookieMessage";
import { handleOnBlurMixpanel } from "@utils/mixpanelEvents/mixpanelSignupFormEvent";
import EmailContainer from "./components/EmailContainer";

import styles from "./SignUpForm.module.scss";
import SpLink from "../shared/SpLink";
import SECURE_SIMPLEPRACTICE_SIGNIN from "@constants/secureSpSignIn";
import OtherProfessionField from "./components/OtherProfessionField";
import { useSpecialties } from "@hooks/useSpecialties";
import { SignUpTermsQuery } from "@StaticQueries/SignUpTerms.query";
import { RedirectDataType } from "./helpers/submitForm";
import { useEmailStore } from "@sp-state/useEmailStore";
import { useKount } from "@hooks/useKount";
import { trackMxpEvent } from "@utils/mixpanelEvents/mixpanelEvents";

// Form type
type SignUpFormType = {
  page?: string;
  signUpTitle?: string;
  ctaCopy?: string;
  submitedUserEmail?: string;
  specialtiesId?: number;
  smartDisplayToggle?: "on" | "off";
  smartDisplayOptions?: {
    option: string;
  }[];
  redirectData?: RedirectDataType;
};

const SignUpForm: React.FC<SignUpFormType> = ({
  page = "trialSignUp",
  signUpTitle,
  smartDisplayToggle = "off",
  smartDisplayOptions,
  specialtiesId = 0,
  submitedUserEmail,
  ctaCopy,
  redirectData,
}) => {
  const kountSessionId = useKount();

  const { validateOne, onSubmitForm, deepClone, checkPassword } = Helpers;
  const recaptchaRef = useRef();
  const [formData, setFormData] = useState<SignUpFormData>(
    deepClone<SignUpFormData>(formDataInitialState)
  );
  const [email, setEmail] = useState<FormValue & FormMixPanel>({
    ...initialMXPInputState,
    value: initialInputState.value,
  });
  const [initialTime, setInitialTime] = useState<number>(0);
  const { email: storedEmail } = useEmailStore(state => state);

  const [emailError, setEmailError] = useState<FormError>({
    validation: {
      ...initialInputState.validation,
    },
  });

  const signUpTermsData = SignUpTermsQuery();

  const [hasLicense, setLicense] = useState(false);
  const [pWordTouched, setPWordTouched] = useState(false);
  const [pwIsShow, SetPwIsShow] = useState(false);
  const [formIsSubmitting, setFormIsSubmitting] = useState(false);
  const specialtyRef = useRef<HTMLSelectElement>(null);
  const promocode = Cookie.checkCookie("PROMOCODE")
    ? Cookie.getCookie("PROMOCODE")
    : null;

  useEffect(() => {
    setTimeout(() => {
      window.mixpanelTrialSent = false;
    }, 100);

    setInitialTime(Date.now());

    if (storedEmail) {
      setEmail(prev => ({
        ...prev,
        value: storedEmail,
      }));
    }

    return () => {
      setFormData(deepClone<SignUpFormData>(formDataInitialState));
      setEmail(deepClone<FormInput>(initialInputState));
      setPWordTouched(false);
      SetPwIsShow(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { specialties } = useSpecialties(specialtiesId);

  const [clickedPlan] = useQueryParam("plan", StringParam);
  const simplifyTextUnderButton = "No credit card required.";

  const formSubmitHandler = (e: React.FormEvent<HTMLFormElement>) => {
    setFormIsSubmitting(true);

    const finalTime = Math.floor((Date.now() - initialTime) / 1000);

    onSubmitForm(
      e,
      formData,
      setFormData,
      email,
      emailError,
      setEmailError,
      clickedPlan,
      recaptchaRef,
      smartDisplayToggle,
      specialtyRef.current,
      kountSessionId,
      finalTime,
      redirectData
    )
      .then(() => {
        // Add Mixpanel events here!
        if (smartDisplayToggle === "off") return;

        let specialtyLabel;

        specialties.forEach(option => {
          if (formData.globalProfessionIds.value === option.value) {
            specialtyLabel = option.label;
          }
        });

        trackMxpEvent("user: smart display sign up - license/certification #", {
          Page: window.location.pathname,
          License: formData.license.value,
          Email: email.value,
          Speciality: specialtyLabel,
        });

        trackMxpEvent("user: smart display sign up - user intent", {
          Page: window.location.pathname,
          Intent: formData.smartDisplay.value,
          Email: email.value,
          Speciality: specialtyLabel,
        });
      })
      .finally(() => {
        setFormIsSubmitting(false);
      });

    if (!pWordTouched) setPWordTouched(true);
  };

  const handleCheckPassword = (e: any) =>
    checkPassword(e.target.value, formData, setFormData);

  const handleKeypress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { name } = e.currentTarget;
    if (!formData[name].isUpdated) {
      setFormData({
        ...formData,
        [name]: {
          ...formData[name],
          isUpdated: true,
        },
      });
    }
  };

  const handleInputChange = e => {
    const { name, type } = e.target;
    const value = type === "checkbox" ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [name]: {
        ...formData[name],
        isUpdated: true,
        value,
        validation: {
          hasError: false,
          message: "",
        },
      },
    });

    if (name === "notApplicable") {
      setFormData(prevState => ({
        ...prevState,
        license: {
          ...prevState.license,
          value: "",
          validation: {
            hasError: false,
            message: "",
          },
        },
      }));

      if (formData.notApplicable.value) {
        setLicense(false);
      } else {
        setLicense(true);
      }
    }
  };

  const onBlurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
    validateOne(e, formData, setFormData);
    handleOnBlurMixpanel({ e, formData, setFormData });
  };

  const handleOnFocus = (
    e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    if ("target" in e) {
      const { name } = e["target"];
      setFormData({
        ...formData,
        [name]: {
          ...formData[name],
          timeIn: Date.now(),
        },
      });
    }
  };

  const onPasswordBlur = e => {
    onBlurHandler(e);
    if (!pWordTouched) setPWordTouched(true);
  };

  const toggleShowPassword = () => SetPwIsShow(!pwIsShow);

  const handleOnBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>
  ) => handleOnBlurMixpanel({ e, formData, setFormData });

  const buttonLoadingStyles = formIsSubmitting
    ? styles.signUpSubmitLoading
    : "";

  const showPasswordText = pwIsShow ? "Hide" : "Show";
  const showPasswordIcon = pwIsShow ? true : false;

  const formPromoStyle = promocode
    ? { borderTopLeftRadius: 0, borderTopRightRadius: 0 }
    : null;

  const {
    firstNameValidationClass,
    lastNameValidationClass,
    firstAndLastNameValidationClass,
    agreementValidationClass,
    phoneNumberLabelValidationClass,
    passwordValidationClass,
    phoneNumberValidationClass,
    emailValidationClass,
    checkboxClass,
    licenseValidationClass,
    licenseLabelValidationClass,
    notApplicableValidationClass,
    notApplicableCheckboxClass,
    smartDisplayValidationClass,
    globalProfessionsValidationClass,
  } = validationClasses(formData, styles, emailError);

  const termsErrorText = signUpTermsData?.termsAgreementErrorText ? (
    <p
      className={styles.activeError}
      dangerouslySetInnerHTML={{
        __html: signUpTermsData.termsAgreementErrorText!,
      }}
    />
  ) : (
    <p className={styles.activeError}>
      {formData.agreement.validation.message}
    </p>
  );

  return (
    <div className={pageOptions[page]?.styles}>
      {promocode && <FormCookieMessage cookie={promocode} />}
      <form style={formPromoStyle} onSubmit={formSubmitHandler}>
        {signUpTitle && (
          <>
            <h3 className="h3"> {signUpTitle} </h3>
            <p className="body primary--grey-text" />
            <span className="hr  hr--24 " />
            <span className="hr hr--8" />
          </>
        )}

        <label htmlFor="first_name">Full Name</label>
        <div className={firstAndLastNameValidationClass}>
          <div
            className={`${styles.inputContainer} ${styles.inputContainerFirstName}`}
          >
            <input
              type="text"
              name="first_name"
              placeholder="First name"
              value={formData["first_name"].value}
              onChange={handleInputChange}
              className={firstNameValidationClass}
              onBlur={onBlurHandler}
              onFocus={handleOnFocus}
              onKeyPress={handleKeypress}
            />
            {formData["first_name"].validation.hasError && (
              <>
                <div className={styles.errorIcon} />
                <p className={styles.activeError}>
                  {formData["first_name"].validation.message}
                </p>
              </>
            )}
          </div>
          <div
            className={`${styles.inputContainer} ${styles.inputContainerLastName}`}
          >
            <input
              type="text"
              name="last_name"
              value={formData["last_name"].value}
              onChange={handleInputChange}
              onBlur={onBlurHandler}
              className={lastNameValidationClass}
              placeholder="Last name"
              onFocus={handleOnFocus}
              onKeyPress={handleKeypress}
            />
            {formData["last_name"].validation.hasError && (
              <>
                <div className={styles.errorIcon} />
                <p className={styles.activeError}>
                  {formData["last_name"].validation.message}
                </p>
              </>
            )}
          </div>
        </div>

        <EmailContainer
          email={email}
          emailError={emailError}
          emailValidationClass={emailValidationClass}
          setEmail={setEmail}
          setEmailError={setEmailError}
          styles={styles}
          submitedUserEmail={submitedUserEmail}
        />

        <label htmlFor="password">Password</label>
        <div className="pos--relative">
          <input
            className={` ${styles.pwInputMargin} ${passwordValidationClass} pos--relative `}
            type={pwIsShow ? "text" : "password"}
            id="password"
            name="password"
            value={formData.password.value}
            onChange={handleInputChange}
            onKeyUp={handleCheckPassword}
            onBlur={onPasswordBlur}
            autoComplete="new-password"
            onFocus={handleOnFocus}
            onKeyPress={handleKeypress}
          />
          <button
            type="button"
            onClick={toggleShowPassword}
            className={`${styles.pwShow} ${
              showPasswordIcon ? styles.showPasswordIcon : ""
            }`}
          >
            <div />
            <p>{showPasswordText}</p>
          </button>
        </div>

        <PasswordValidationLabel
          beenTouched={pWordTouched}
          validation={formData.password.passwordValidation}
        />

        <label htmlFor="phone_number">Mobile phone</label>
        <div className={styles.inputContainer}>
          <PhoneInputMask
            onChange={handleInputChange}
            onInputBlur={onBlurHandler}
            onInputFocus={handleOnFocus}
            placeholder="(___) ___-____"
            name="phone_number"
            className={phoneNumberValidationClass}
            value={formData["phone_number"].value}
          />
          {formData["phone_number"].validation.hasError && (
            <div className={styles.errorIcon} />
          )}
        </div>
        <p className={phoneNumberLabelValidationClass}>
          For text verification to access your account.
        </p>
        {formData["phone_number"].validation.hasError && (
          <p className={styles.activeError}>
            {formData["phone_number"].validation.message}
          </p>
        )}

        {smartDisplayToggle === "on" && (
          <hr className={styles.signUpAlterDivider} />
        )}

        <label>What best describes your practice?</label>
        <select
          value={formData.globalProfessionIds.value}
          ref={specialtyRef}
          onChange={handleInputChange}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          name="globalProfessionIds"
          className={globalProfessionsValidationClass}
        >
          <option value="" disabled>
            Select One
          </option>
          {specialties.length > 0 &&
            specialties.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
        </select>

        {!formData.globalProfessionIds.value &&
          formData.globalProfessionIds.validation.hasError && (
            <p className={styles.activeError}>
              {formData.globalProfessionIds.validation.message}
            </p>
          )}

        <OtherProfessionField
          globalProfession={formData.globalProfessionIds.value}
          setFormData={setFormData}
          formData={formData}
        />
        {page === "SignUpV1" && <span className="hr hr--10" />}

        {smartDisplayToggle === "on" && (
          <div>
            <label>License/Certification #</label>
            <div className={styles.inputContainer}>
              <input
                disabled={hasLicense}
                type="text"
                onBlur={onBlurHandler}
                onChange={handleInputChange}
                name="license"
                value={formData.license.value}
                className={licenseValidationClass}
              />
              {!formData.license.value &&
                formData.license.validation.hasError && (
                  <div className={styles.errorIcon} />
                )}
              <p
                className={`${licenseLabelValidationClass} ${styles.formLabelLicense}`}
              >
                Used to optimize your experience.
              </p>
            </div>
            {!formData.license.value &&
              formData.license.validation.hasError && (
                <p
                  className={`${styles.activeError} ${styles.activeErrorLicense}`}
                >
                  {formData.license.validation.message}
                </p>
              )}

            <div
              className={`${styles.agreementCheckbox} ${styles.notApplicableContainer} ${notApplicableValidationClass}`}
            >
              <div className={styles.signUpCheckboxContainer}>
                <input
                  className={notApplicableCheckboxClass}
                  type="checkbox"
                  checked={Boolean(formData.notApplicable.value)}
                  name="notApplicable"
                  onChange={handleInputChange}
                />
              </div>
              <div className={styles.notApplicableCheckboxLabel}>
                Not applicable
              </div>
            </div>

            <div>
              <label>
                What are you looking to get out of SimplePractice today?
              </label>
              <select
                value={formData.smartDisplay.value}
                onChange={handleInputChange}
                name="smartDisplay"
                className={smartDisplayValidationClass}
              >
                <option value="" disabled>
                  Select One
                </option>
                {smartDisplayOptions.length > 0 &&
                  smartDisplayOptions.map((option, index) => (
                    <option key={index} value={option.option}>
                      {option.option}
                    </option>
                  ))}
              </select>

              {!formData.smartDisplay.value &&
                formData.smartDisplay.validation.hasError && (
                  <p className={styles.activeError}>
                    {formData.smartDisplay.validation.message}
                  </p>
                )}
            </div>
          </div>
        )}

        <div
          className={`${styles.agreementCheckbox} ${agreementValidationClass}`}
        >
          <div className={styles.signUpCheckboxContainer}>
            <input
              className={checkboxClass}
              type="checkbox"
              checked={formData.agreement.value}
              name="agreement"
              onChange={handleInputChange}
              onFocus={handleOnFocus}
              onBlur={handleOnBlur}
            />
          </div>
          <TermsAndServices
            termsAgreementText={signUpTermsData?.termsAgreementText}
          />
        </div>
        {!formData.agreement.value &&
          formData.agreement.validation.hasError &&
          termsErrorText}
        <HorizontalRule variant="hr--24" />
        <ReCAPTCHA
          sitekey={process.env.GATSBY_GRECAPTCHA_SITE_KEY}
          ref={recaptchaRef}
          size="invisible"
        />

        <button className={`${styles.signUpSubmit} ${buttonLoadingStyles}`}>
          <p>{ctaCopy ? ctaCopy : pageOptions[page]?.buttonText}</p>
        </button>

        {signUpTitle && (
          <p className={styles.simplifyUnderButton}>
            {simplifyTextUnderButton}
          </p>
        )}

        <p className={styles.formSignInCopy}>
          Already have an account?{" "}
          <SpLink path={SECURE_SIMPLEPRACTICE_SIGNIN}>Sign in</SpLink>.
        </p>
      </form>
    </div>
  );
};

export default SignUpForm;
